import { BoldText, DangerText, MuteText } from '@elements/Common';
import { FileUploadComponent } from '@components/FileUploadComponent';
import { useEffect, useState } from 'react';
import { Button, Checkbox, Modal, TextField } from '@skupreme/shared/ui';
import UploadService from '@utility/services/upload';
import { ToastMessage } from '@utility/toast';
import OrganizationsService from '@utility/services/organization';
import {
  ErrorMessageConstant,
  OrganizationType,
} from '@utility/constants/common';
import { LoaderBar } from '@components/LoaderBar';
import { useAuth } from '@contexts/auth';
import { useNavigate } from 'react-router';
import UserService from '@utility/services/user';
import type { FileItem } from 'types/components/file-upload';

type PopupWorkspaceProps = {
  open: boolean;
  closePopup: () => void;
};

export function PopupWorkspace({ open, closePopup }: PopupWorkspaceProps) {
  const { subscription, redirect } = useAuth();
  const navigate = useNavigate();
  const { login: providerLogin, currentOrg } = useAuth();

  const [loading, setLoading] = useState(false);

  const [steps, setSteps] = useState([
    {
      key: 'shipping',
      title: 'Workspace',
      active: true,
    },
    {
      key: 'billing',
      title: 'Pricing',
    },
  ]);

  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [error, setError] = useState('');

  const [fields, setFields] = useState({
    name: '',
    email: '',
    type: false,
    company_name: '',
  });

  const closeModal = () => {
    resetFields();
    closePopup();
  };

  const resetFields = () => {
    setFiles([]);
    setError('');
    setSteps([
      {
        key: 'shipping',
        title: 'Workspace',
        active: true,
      },
      {
        key: 'billing',
        title: 'Pricing',
      },
    ]);
    setFields({
      name: '',
      email: '',
      company_name: '',
      // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'existing_wallet' does not...
      existing_wallet: true,
    });
    const plan = subscription?.price_id;
    // @ts-expect-error - TS2345: Argument of type 'string | undefined' is not assignable to parameter of type 'Se...
    setCurrentPlan(plan);
  };

  useEffect(() => {
    resetFields();
  }, []);

  const handleApiSuccess = async res => {
    ToastMessage.success('New Workspace Created!');
    providerLogin(res);
    closeModal();
    if (!UserService.isSuperAdmin(res?.user)) {
      navigate('/app/dashboard/awaiting-actions');
    } else {
      // @ts-expect-error - TS2554: Expected 1 arguments, but got 0.
      redirect();
    }
  };

  const uploadClick = async data => {
    if (steps[1].active && !currentPlan) {
      setError('Select a Plan');
      return;
    }

    if (!fields.company_name) {
      setError('Company Name is required');
      return;
    }

    setLoading(true);
    let pathKey = null;
    let bucketName = null;
    if (files.length > 0) {
      /* @ts-expect-error - TS2339: Property 'key' does not exist on type '{ key: any; bucket_na... */
      const { key, bucket_name } = await upload();
      pathKey = key;
      bucketName = bucket_name;
    }
    if (files.length > 0 && !pathKey) {
      ToastMessage.error('There was some error uploading image');
    } else {
      const payload = {
        ...data,
        logo: {
          key: pathKey,
          bucket_name: bucketName,
        },
        users: [],
        company_name: fields.company_name,
      };

      // @ts-expect-error - TS2339: Property 'existing_wallet' does not exist on type '{ name: string; email: string...
      if (fields.existing_wallet) {
        payload.wallet_organization_id = currentOrg;
      }
      if (!pathKey) delete payload.logo;
      if (fields.type) {
        payload.type = OrganizationType.supplier;
      }
      try {
        const res = await OrganizationsService.add(payload);
        handleApiSuccess(res);
      } catch (error) {
        ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
      } finally {
        setLoading(false);
      }
      setFiles([]);
    }
    setLoading(false);
  };

  const upload = async () => {
    const newFiles = files.map(x => x.file);
    try {
      if (newFiles.length > 0) {
        const file = newFiles[0];
        const { uploadUrl, key, bucket } =
          await OrganizationsService.logoSignedUrl({
            mime_type: file.type,
            public: true,
          });
        await UploadService.media(uploadUrl, file, file.type);
        return {
          key,
          bucket_name: bucket,
        };
      }
    } catch (error) {
      return {
        key: null,
        bucket_name: null,
      };
    }
  };

  const setField = (key, value) => {
    setFields({
      ...fields,
      [key]: value,
    });
  };

  useEffect(() => {
    const plan = subscription?.price_id;
    // @ts-expect-error - TS2345: Argument of type 'string | undefined' is not assignable to parameter of type 'Se...
    setCurrentPlan(plan);
  }, []);

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && closeModal()}
      size="md"
      title="Add New Workspace"
      showCloseButton
      isDismissable={!loading}
      footer={
        <>
          <Button size="lg" onPress={() => closeModal()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            // @ts-expect-error - TS2554: Expected 1 arguments, but got 0.
            onPress={() => uploadClick()}
            isDisabled={!currentPlan}
          >
            Create
          </Button>
        </>
      }
    >
      {loading && <LoaderBar content="Creating your Workspace!" />}
      <div className="sk:flex sk:flex-col sk:gap-4">
        <div className="sk:flex sk:flex-col sk:gap-1.5">
          <span id="workspace-company-name" className="sk:text-xs sk:text-fg">
            Company Name
          </span>
          <TextField
            aria-labelledby="workspace-company-name"
            placeholder="Company Name"
            value={fields.company_name}
            onChange={value => setField('company_name', value)}
            className="sk:w-full"
          />
          {error && <DangerText>{error}</DangerText>}
        </div>
        <div className="sk:flex sk:flex-col sk:gap-1.5">
          <span>
            Company Logo&nbsp;
            <MuteText>(optional)</MuteText>
          </span>
          <FileUploadComponent
            containerHeight={120}
            files={files}
            setFiles={setFiles}
            accept=".jpg,.tif,.jpeg,.png"
          />
        </div>
        <div className="sk:flex sk:flex-col sk:gap-2">
          <BoldText>Options</BoldText>
          <Checkbox
            isSelected={fields.type}
            onChange={isSelected => setField('type', isSelected)}
          >
            Supplier Type
          </Checkbox>
        </div>
        {!currentPlan && <DangerText>No subscription plan found</DangerText>}
      </div>
    </Modal>
  );
}
